import { tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { generatePath, useNavigate } from 'react-router-dom';
import { ParkTimeContractType, type Parking, type ParkTimeContract } from '@repo/domain';
import { usePriceDebounced } from '@repo/domain-service';
import { isFeatureFlagEnabled } from '@repo/feature-flag';
import { PackagesSection, ParkingHeaderContainer, ParkingInformationCard, PropertiesCard } from '@repo/entities';
import { useMemo } from 'react';

import { PACKAGES_SECTION_ID } from '@features/reservation/constants';
import { useReservationConsentPrefetch } from '@features/reservation/hooks/useReservationConsentPrefetch';
import { useStartPackageReservation } from '@features/reservation/hooks/useStartPackageReservation';

import { useSelectedParkingProperties } from '@shared/hooks/useSelectedParkingProperties';
import PageFrame from '@shared/components/page/PageFrame';
import PleaseWaitAnimation from '@shared/components/PleaseWaitAnimation';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

const HOUR_IN_MINUTES: number = 60 as const;

interface Props {
  parking: Parking;
  testId: TestId;
  spotId?: string;
}

export default function QrContents({ parking, testId, spotId }: Props) {
  const navigate = useNavigate();
  const parkTimeContract: ParkTimeContract = useMemo(
    () => ({
      type: ParkTimeContractType.FixedDuration,
      duration: HOUR_IN_MINUTES, // Goal is to show price for one hour (as standard period).
    }),
    []
  );
  const selectedProperties = useSelectedParkingProperties();
  const { price, isError } = usePriceDebounced(parking.id, parkTimeContract, spotId, selectedProperties);
  const showPackages = isFeatureFlagEnabled('parkingPackages');
  const shouldDisplayProperties = !!parking.properties;
  useReservationConsentPrefetch();

  const handleClose = () => {
    navigate(ROUTE_PATHS[RouteName.MAP]);
  };

  const handleReserveNow = () => {
    navigate(generatePath(ROUTE_PATHS[RouteName.RESERVATION_DURATION], { id: parking.id }));
  };

  const startPackageReservation = useStartPackageReservation();

  const handleSelectPackage = (priceRuleId: string): void => {
    startPackageReservation({ parkingId: parking.id, parkingSlug: parking.slug, priceRuleId });
  };

  if (!price) return <PleaseWaitAnimation testId={testId} />;

  return (
    <PageFrame
      testId={testId}
      onClose={handleClose}
      forwardButton={{
        label: price.estimated ? tr('reservations:set_duration') : tr('reservations:spot_taken'),
        disabled: !price.estimated,
        onClick: handleReserveNow,
      }}
    >
      <ParkingHeaderContainer
        parking={parking}
        price={price}
        parkDuration={HOUR_IN_MINUTES}
        parkStartAtTimestamp={undefined}
        onGoBack={handleClose}
        isPriceError={isError}
        testId={testId.append('parkingDetails')}
        packagesSectionId={PACKAGES_SECTION_ID}
      />
      {shouldDisplayProperties && (
        <PropertiesCard properties={parking.properties} testId={testId.append('properties')} showHelperText />
      )}
      <ParkingInformationCard parking={parking} testId={testId.append('parkingInfo')} withGallery />
      {showPackages && parking.hasPackages && (
        <div id={PACKAGES_SECTION_ID} className="mt-3 scroll-mt-16">
          <PackagesSection
            testId={testId.append('packages')}
            packages={parking.packages}
            onSelectPackage={handleSelectPackage}
          />
        </div>
      )}
    </PageFrame>
  );
}
